/**
 * Create New Hearing — the form the Technical Delegate opens a disciplinary case on.
 *
 * `role="dialog" aria-modal="true"`, closing on Cancel, Close, backdrop click or Escape.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REBUILT ON 17 AUGUST 2026, AFTER THE TECHNICAL DELEGATE USED IT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The first build of this form asked the TD to type almost everything, because the team lists
 * and the fixtures were not reachable from it, and it explained each gap in the builder's own
 * words ("the TMS team list is not loaded, so…"). A form that says why it cannot help you is
 * still a form that cannot help you. It now offers what it knows:
 *
 *  · **Team** is a dropdown of the tournament's teams for the chosen competition, with a
 *    typed-code fallback for the case the registry does not cover.
 *  · **Team Members Involved** asks the ROLE FIRST and then offers that team's people with
 *    that role by name. Choosing a player fills in the shirt number and the gender; choosing a
 *    team official fills in the gender and leaves the shirt blank, because a team official
 *    does not wear one. There is a by-hand fallback on every row, and a row filled in by hand
 *    is FLAGGED as such on the record rather than being indistinguishable from a picked one.
 *  · **Match Number** is a dropdown of that competition's fixtures, each reading
 *    `M09 · AZU v CRV · 14 Aug 10:00`, and choosing one fills the match date in.
 *  · Every moment — the incident, the report, the hearing — is a **date box and a time box**
 *    side by side, so setting the time cannot re-open the calendar (`DateTimeField`).
 *  · A refused submit **marks the fields at fault**, one message under each, and puts the
 *    cursor in the first of them, instead of printing one line at the foot of a long form.
 *  · **Location Of The Hearing** arrives filled in from the venue in Tournament Configuration.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT DID *NOT* CHANGE, AND MUST NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **The form never forces a false match-time claim (ruling §J).** A case can be recorded with
 * no match at all; the off-pitch phase still disables and clears the minute, because a minute
 * is a match-time claim and that phase denies one, and the route still refuses the pair.
 *
 * **Ruling §K — the record is a snapshot.** Picking a person off a team list copies their
 * name, role, shirt number and gender ONTO the case as text, and sends the squad row's id
 * only as a reference. A team list re-loaded next week can never rewrite a disciplinary
 * record, which is the whole reason the snapshot exists.
 *
 * **The charge fields are `ChargePicker.tsx`'s.** Where the bound Code catalogue has been
 * loaded it offers the articles; where it has not, it asks for the article, its level and its
 * wording by hand and says so on the control. Either way the case carries the QUOTED WORDING,
 * because that snapshot is what the letter, the minutes and the decision quote.
 *
 * **The incident time and the time the report arrived are both required and neither is ever
 * defaulted to "now".** The 24-hour rule is computed from them, and a report time quietly set
 * to the present would mark a genuinely late report as within time.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  HEARING_MATCH_PHASES,
  incidentSentence,
  OFF_PITCH_PHASE,
  type TmsCompetitionId,
} from "@hockeytd/shared";

import type {
  CreateHearingRequest,
  FixtureRowView,
  HearingChargeView,
  HearingMatchPhase,
  HearingPersonRole,
  HearingPersonView,
  TeamMemberView,
  TeamView,
} from "../../api/types.js";
import { CountryFlag } from "../../app/CountryFlag.js";
import { ChargePicker, emptyCharge, type ChargeDraft } from "./ChargePicker.js";
import { combineDateAndTime, DateTimeField } from "./DateTimeField.js";

/** The three validation messages the specification fixes, verbatim. */
export const CREATE_ERRORS = {
  noPerson: "Select at least one team member involved.",
  noCharge: "Charge 1 is required — pick the article breached.",
  noDetails: "Incident details are required — they are carried into the letter.",
} as const;

/**
 * A fourth message the specification does not have, because its control could not produce
 * the fault.
 *
 * In the specification the article's wording arrives with the article, out of the bound
 * catalogue, so a charge is either picked or it is not. Where the catalogue has not been
 * loaded the wording is typed, so a charge can be half given — and that must be refused with
 * a message naming the real reason rather than borrowing `noCharge`, which would tell the TD
 * to pick an article he has already picked.
 */
export const CHARGE_WORDING_ERROR =
  "Charge 1 needs the article wording — the bound Code catalogue is not loaded, so it is quoted in by hand.";

export const INSTANT_ERROR =
  "The time of the incident and the time the report was received are both required — the 24-hour rule is computed from them.";

/** Said under a date/time pair with one half filled in. Two boxes can hold half a moment. */
export const HALF_INSTANT_ERROR = {
  date: "Give the date as well as the time.",
  time: "Give the time as well as the date.",
} as const;

/**
 * THE LIVE INCIDENT-SENTENCE PREVIEW (§14, ruled by the Technical Delegate on 2026-09-10).
 *
 * Live testing produced a Hearing Letter reading *"…player ABC #15 The player swung his
 * stick…"*: the TD had written his details as a whole sentence, and §10c's generator — which
 * never rewrites them — printed them straight after the name it had just composed. The
 * generator is right to leave the TD's words alone, so the fix is at capture: the form shows
 * the composed sentence as it types, and the TD sees the double subject before a case exists.
 *
 * The preview is composed by `incidentSentence` from `@hockeytd/shared`, which is the SAME
 * function the Hearing Letter composes with. A second copy here would be a preview that can
 * drift from the letter it claims to preview — worse than none, because the TD would be
 * reading a sentence he is not in fact about to create.
 */
export const PREVIEW_LABEL = "How The Letter Will Read";

/** The helper line under the preview. Plain TD language, and it shows the fault by example. */
export const DETAILS_HINT =
  "The letter names the person for you — write only what happened, continuing from the name: " +
  "“swung his stick at…”, not “The player swung…”.";

/** Said in place of the sentence while nothing has been written. */
export const PREVIEW_EMPTY =
  "Nothing written yet — the sentence appears here as you type, exactly as the letter will carry it.";

/** The one line at the foot, kept as the summary the reader's eye lands on after a refusal. */
export const SUMMARY_ERROR =
  "Some answers are missing. The fields to fix are marked in red above.";

/**
 * The phases, imported rather than restated (ruling §J). This list, the route's validator and
 * the database enum were three separate declarations; a form offering a value the route
 * refuses is a form that fails on submit, so there is now one list and all three read it.
 */
const PHASES: readonly HearingMatchPhase[] = HEARING_MATCH_PHASES;

/**
 * THE TWO ROLES THE FORM OFFERS, AND WHY THERE ARE NOT THREE.
 *
 * A team list has exactly two kinds of person: a player, and everybody else on the bench —
 * coaches, physiotherapists, doctors, team managers. The TD asked for those two words and no
 * others, because at the moment of opening a case that is the distinction that matters: one
 * wears a shirt number and the other does not.
 *
 * The stored vocabulary still admits `Coach` as its own value, and nothing here removes it —
 * a case created before today keeps it and prints it. What the office actually was is not
 * lost either: the squad row carries it in words (`Coach`, `Physiotherapist`, …) and the
 * dropdown prints it beside the name, so the TD picks `SOLBERG Marit · Physiotherapist`
 * rather than guessing which of three roles a physiotherapist belongs under.
 */
const FORM_ROLES: readonly HearingPersonRole[] = ["Player", "Team Official"];

/** The person role a squad row's role maps onto. */
function personRoleOf(role: TeamMemberView["role"]): HearingPersonRole {
  return role === "player" ? "Player" : "Team Official";
}

/** The squad role a person role maps back onto, for filtering the name dropdown. */
function memberRoleOf(role: HearingPersonRole): TeamMemberView["role"] {
  return role === "Player" ? "player" : "team_official";
}

/** `SOLBERG Marit · 14` for a player, `SOLBERG Marit · Physiotherapist` for an official. */
export function memberOptionLabel(member: TeamMemberView): string {
  if (member.role === "player") {
    return member.shirtNumber === null
      ? member.displayName
      : `${member.displayName} · ${String(member.shirtNumber)}`;
  }
  return member.officialTitle === null
    ? member.displayName
    : `${member.displayName} · ${member.officialTitle}`;
}

/**
 * WHICH OF A TEAM'S OFFICIALS IS THE TEAM MANAGER.
 *
 * `team_member.official_title` is free text, not an enum (ruling §K, migration 034), so the
 * office is matched case-insensitively and `Manager` on its own counts — a team list that
 * says `manager` means the same person a team list that says `Team Manager` means. Where
 * several rows claim the office the first is taken and the TD can change it; where none
 * does, nothing is pre-filled and the field is left for him to answer.
 */
export function findTeamManager(
  members: readonly TeamMemberView[],
): TeamMemberView | null {
  return (
    members.find((member) => {
      if (member.role !== "team_official") return false;
      const title = (member.officialTitle ?? "").trim().toLowerCase();
      return title === "team manager" || title === "manager";
    }) ?? null
  );
}

/** `M09 · AZU v CRV · 14 Aug 10:00` — what the TD reads off the match schedule on the wall. */
export function fixtureOptionLabel(fixture: FixtureRowView): string {
  // A classification fixture can be drawn before its teams are known. `?` says so plainly;
  // inventing a code here would put the wrong team on a disciplinary form.
  const teams = `${fixture.homeTeamCode ?? "?"} v ${fixture.awayTeamCode ?? "?"}`;
  // RUN 83 QA — a calendar date is read and printed in UTC, the way `caseText.formatMatchDay`
  // and `matchDays.dayLabel` do it. Parsing and formatting both in the DEVICE's zone cancelled
  // out, so this printed the right day; it was the one place in the tree still doing it, and
  // correcting either half alone would have turned it into an off-by-one on the match picker of
  // a disciplinary form.
  const parsed = new Date(`${fixture.date}T00:00:00Z`);
  const day = Number.isNaN(parsed.getTime())
    ? fixture.date
    : new Intl.DateTimeFormat("en-GB", {
        timeZone: "UTC",
        day: "numeric",
        month: "short",
      }).format(parsed);
  // The kick-off is carried as `HH:MM:SS`; the schedule on the wall says `10:00`.
  return `${fixture.displayMatchNo} · ${teams} · ${day} ${fixture.time.slice(0, 5)}`;
}

/** The sentinel the Team dropdown uses for "not on this list — I will type the code". */
const TYPED_TEAM = "__typed__";
/** The sentinel a name dropdown uses for "not on the list — I will type this person in". */
const TYPED_PERSON = "__typed__";
/** The Team Manager dropdown's "not on the list — I will type the name" option. */
const TYPED_MANAGER = "__typed__";
/** The Match Number dropdown's two non-fixture options. */
const NO_MATCH = "";
const TYPED_MATCH = "__typed__";

interface PersonDraft {
  /** The squad row picked, or null when the row was typed by hand. */
  teamMemberId: string | null;
  /** True once this row was filled in by hand — carried onto the record (ruling §K). */
  enteredByHand: boolean;
  name: string;
  role: HearingPersonRole;
  shirtNumber: string;
  gender: "m" | "f";
}

function emptyPerson(): PersonDraft {
  return {
    teamMemberId: null,
    enteredByHand: false,
    name: "",
    role: "Player",
    shirtNumber: "",
    gender: "m",
  };
}

/** Every field this form can refuse, so a message and a focus target share one spelling. */
export type CreateField =
  | "people"
  | "charge"
  | "details"
  | "incidentAt"
  | "reportReceivedAt"
  | "hearingAt";

export interface CreateHearingModalProps {
  onCancel: () => void;
  onCreate: (input: CreateHearingRequest) => void;
  /** True while the POST is in flight. */
  busy: boolean;
  /** A refusal from the API, shown beside the validation line. */
  error: Error | null;
  /** The tournament's teams, both competitions. Empty when the read failed — see the control. */
  teams: readonly TeamView[];
  /** The tournament's fixtures, both competitions. Empty when the read failed. */
  fixtures: readonly FixtureRowView[];
  /** The hearing venue from Tournament Configuration, or null when none is configured. */
  defaultLocation: string | null;
  /** Reads one team's squad. Called when a team and a role have both been chosen. */
  loadMembers: (
    competitionId: TmsCompetitionId,
    teamCode: string,
  ) => Promise<readonly TeamMemberView[]>;
}

export function CreateHearingModal({
  onCancel,
  onCreate,
  busy,
  error,
  teams,
  fixtures,
  defaultLocation,
  loadMembers,
}: CreateHearingModalProps): React.JSX.Element {
  const [competitionId, setCompetitionId] = useState<TmsCompetitionId>("1916");
  const [teamChoice, setTeamChoice] = useState("");
  const [typedTeamCode, setTypedTeamCode] = useState("");
  const [people, setPeople] = useState<PersonDraft[]>([emptyPerson()]);
  const [matchChoice, setMatchChoice] = useState<string>(NO_MATCH);
  const [typedMatchRef, setTypedMatchRef] = useState("");
  const [matchDate, setMatchDate] = useState("");
  const [phase, setPhase] = useState<HearingMatchPhase>("Not Specified");
  const [matchMinute, setMatchMinute] = useState("");
  /** Ruling §J: this phase asserts the incident was not during play, so it carries no minute. */
  const offPitch = phase === OFF_PITCH_PHASE;
  const [charges, setCharges] = useState<ChargeDraft[]>([
    emptyCharge(),
    emptyCharge(),
    emptyCharge(),
  ]);
  const [incidentDetails, setIncidentDetails] = useState("");
  const [incidentDate, setIncidentDate] = useState("");
  const [incidentTime, setIncidentTime] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [reportTime, setReportTime] = useState("");
  const [hearingDate, setHearingDate] = useState("");
  const [hearingTime, setHearingTime] = useState("");
  const [hearingLocation, setHearingLocation] = useState(defaultLocation ?? "");
  /**
   * §10'S ADDRESSEE, ASKED FOR WHERE THE CASE IS OPENED.
   *
   * The Hearing Letter is written to the Team Manager and `generateHearingLetter` refuses a
   * case that names none — so a form that never asked produced cases that could not draft
   * their own letter, which is what the Technical Delegate met on 19 August 2026. The refusal
   * is unchanged; what changed is that the answer is now collected at the one moment the TD
   * has the team in front of him.
   *
   * It is NOT required to create a case. A report can reach the TD before he knows who the
   * team manager is, and refusing the case would be the form deciding that a real incident is
   * not recordable — the same trade ruling §J forbids over the match phase. The letter's own
   * refusal remains the gate, and the case file offers the field afterwards.
   */
  const [managerChoice, setManagerChoice] = useState("");
  const [typedManager, setTypedManager] = useState("");

  /** The summary line at the foot, and the per-field messages above it (F-15). */
  const [invalid, setInvalid] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CreateField, string>>>({});

  const peopleRef = useRef<HTMLSelectElement | null>(null);
  const detailsRef = useRef<HTMLTextAreaElement | null>(null);
  const incidentRef = useRef<HTMLInputElement | null>(null);
  const reportRef = useRef<HTMLInputElement | null>(null);
  const hearingRef = useRef<HTMLInputElement | null>(null);
  const chargeRef = useRef<HTMLDivElement | null>(null);

  // Escape closes. Bound on the document so it works wherever focus is.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onCancel]);

  /** The configured venue arrives after the constants read settles, so it fills in then. */
  useEffect(() => {
    if (defaultLocation !== null) {
      setHearingLocation((current) => (current === "" ? defaultLocation : current));
    }
  }, [defaultLocation]);

  const competitionTeams = useMemo(
    () => teams.filter((team) => team.competitionId === competitionId),
    [teams, competitionId],
  );
  const competitionFixtures = useMemo(
    () =>
      fixtures
        .filter((fixture) => fixture.competitionId === competitionId)
        .slice()
        .sort((a, b) => (a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date))),
    [fixtures, competitionId],
  );

  /** The team code actually in play — the picked one, or the typed fallback. */
  const teamCode = teamChoice === TYPED_TEAM ? typedTeamCode.trim().toUpperCase() : teamChoice;

  /**
   * THE CHOSEN TEAM'S SQUAD, read once per team rather than once per person row.
   *
   * A failed read is not a failed form: `members` stays empty, every name dropdown says so on
   * itself, and the by-hand fallback — which exists anyway, for the player who is not on the
   * list — carries the whole form. A team list this system could not read must never be the
   * reason a real incident cannot be recorded.
   */
  const [members, setMembers] = useState<readonly TeamMemberView[]>([]);
  const [membersState, setMembersState] = useState<"idle" | "loading" | "ready" | "failed">(
    "idle",
  );

  useEffect(() => {
    if (teamCode === "" || teamChoice === TYPED_TEAM) {
      setMembers([]);
      setMembersState("idle");
      return;
    }
    let live = true;
    setMembersState("loading");
    loadMembers(competitionId, teamCode).then(
      (rows) => {
        if (!live) return;
        setMembers(rows);
        setMembersState("ready");
      },
      () => {
        if (!live) return;
        setMembers([]);
        setMembersState("failed");
      },
    );
    return () => {
      live = false;
    };
  }, [competitionId, teamCode, teamChoice, loadMembers]);

  /** That team's officials, which is the list the Team Manager is chosen from. */
  const teamOfficials = useMemo(
    () => members.filter((member) => member.role === "team_official"),
    [members],
  );

  /**
   * PRE-FILL THE TEAM MANAGER FROM THE SQUAD, ONCE, AND NEVER OVER AN ANSWER.
   *
   * The office is on the team list, so the TD should not have to know it: reading a squad
   * fills the field in with whoever holds it. It only ever fills an EMPTY field — a name the
   * TD picked or typed is his answer and a squad read must not overwrite it — and where no
   * row claims the office nothing is filled in, because a guessed addressee on a letter that
   * is served on a team is worse than an empty box.
   */
  useEffect(() => {
    if (membersState !== "ready") return;
    setManagerChoice((current) => {
      if (current !== "") return current;
      return findTeamManager(members)?.id ?? "";
    });
  }, [members, membersState]);

  /** Changing competition invalidates the team, and the team invalidates the picked people. */
  function changeCompetition(next: TmsCompetitionId): void {
    setCompetitionId(next);
    setTeamChoice("");
    setMatchChoice(NO_MATCH);
    setPeople((rows) => rows.map(clearPickedPerson));
    if (managerChoice !== TYPED_MANAGER) setManagerChoice("");
  }

  function changeTeam(next: string): void {
    setTeamChoice(next);
    setPeople((rows) => rows.map(clearPickedPerson));
    // A manager picked off the old team's list is not this team's manager. A name typed by
    // hand is left alone — it was never the list's to give and is not the list's to take.
    if (managerChoice !== TYPED_MANAGER) setManagerChoice("");
  }

  /**
   * A person picked off the OLD team is not a person on the new one, so the pick is dropped.
   * A row the TD typed by hand is left exactly as typed — it was never the list's to give and
   * is not the list's to take away.
   */
  function clearPickedPerson(row: PersonDraft): PersonDraft {
    return row.enteredByHand ? row : { ...emptyPerson(), role: row.role };
  }

  const clearField = useCallback((field: CreateField): void => {
    setFieldErrors((current) => {
      if (current[field] === undefined) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  function updatePerson(index: number, patch: Partial<PersonDraft>): void {
    setPeople((rows) => rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
    clearField("people");
  }

  /** Picking a name off the squad copies the person's details onto the row (ruling §K). */
  function pickPerson(index: number, value: string): void {
    if (value === "") {
      updatePerson(index, { ...emptyPerson(), role: people[index]?.role ?? "Player" });
      return;
    }
    if (value === TYPED_PERSON) {
      updatePerson(index, {
        teamMemberId: null,
        enteredByHand: true,
        name: "",
        shirtNumber: "",
      });
      return;
    }
    const member = members.find((row) => row.id === value);
    if (member === undefined) return;
    updatePerson(index, {
      teamMemberId: member.id,
      enteredByHand: false,
      name: member.displayName,
      role: personRoleOf(member.role),
      shirtNumber: member.shirtNumber === null ? "" : String(member.shirtNumber),
      gender: member.gender,
    });
  }

  /** Changing the role changes which half of the squad the name dropdown offers. */
  function changeRole(index: number, role: HearingPersonRole): void {
    const row = people[index];
    if (row !== undefined && !row.enteredByHand) {
      updatePerson(index, { ...emptyPerson(), role });
      return;
    }
    updatePerson(index, {
      role,
      // A team official wears no shirt number, so one typed against a player is dropped
      // rather than carried silently onto a record that would print it.
      ...(role === "Team Official" ? { shirtNumber: "" } : {}),
    });
  }

  function updateCharge(index: number, patch: Partial<ChargeDraft>): void {
    setCharges((rows) => rows.map((row, at) => (at === index ? { ...row, ...patch } : row)));
    clearField("charge");
  }

  /** Choosing a fixture fills the match date in and stops it being editable by hand. */
  function chooseMatch(value: string): void {
    setMatchChoice(value);
    if (value === NO_MATCH || value === TYPED_MATCH) return;
    const fixture = competitionFixtures.find(
      (one) => String(one.tmsMatchDbId) === value,
    );
    if (fixture !== undefined) setMatchDate(fixture.date);
  }

  /** The Team Manager's name as it will be stored — the picked row's, or the typed one. */
  const teamManagerName =
    managerChoice === TYPED_MANAGER
      ? typedManager.trim()
      : (teamOfficials.find((member) => member.id === managerChoice)?.displayName ?? "");

  const chosenFixture =
    matchChoice === NO_MATCH || matchChoice === TYPED_MATCH
      ? null
      : (competitionFixtures.find((one) => String(one.tmsMatchDbId) === matchChoice) ?? null);

  /**
   * The match reference the case will carry, or null for a case that names no match.
   *
   * Hoisted out of `submit` so the preview and the submitted body read the same value: the
   * letter's no-match branch is gated on exactly this (`match === null` ⟺ no reference), and
   * a preview that guessed at it separately could show the wrong branch.
   */
  const matchRef =
    chosenFixture !== null
      ? chosenFixture.displayMatchNo
      : matchChoice === TYPED_MATCH && typedMatchRef.trim() !== ""
        ? typedMatchRef.trim()
        : null;

  /**
   * The minute as the record will hold it — a whole number, or nothing.
   *
   * `Number.parseInt` mirrors the submitted body exactly, including the off-pitch clearing;
   * a minute that parses to nothing is previewed as no minute rather than as `NaN′`.
   */
  const previewMinute = ((): string | null => {
    if (offPitch || matchMinute.trim() === "") return null;
    const parsed = Number.parseInt(matchMinute, 10);
    return Number.isNaN(parsed) ? null : String(parsed);
  })();

  /**
   * §10c's sentence, composed from what the form holds right now.
   *
   * Deliberately built from the WHOLE of what exists rather than waiting for the form to be
   * valid: the TD is reading it to check the wording of his own details, and hiding it until
   * every other field is answered would hide it for exactly as long as it is useful. The
   * details are passed trimmed only — never reworded, never corrected — matching what the
   * body sends and what the generator prints.
   */
  const previewSentence = useMemo(
    () =>
      incidentSentence({
        hasMatch: matchRef !== null,
        matchPhase: phase,
        matchMinute: previewMinute,
        personsInvolved: people
          .filter((person) => person.name.trim() !== "")
          .map((person) => {
            // A half-typed shirt number is previewed as none, because §10c forbids a bare
            // `#` and `#NaN` on a letter would be worse than a name with no number.
            const shirt = Number.parseInt(person.shirtNumber, 10);
            return {
              name: person.name.trim(),
              role: person.role,
              shirtNumber: Number.isNaN(shirt) ? null : shirt,
              gender: person.gender,
            };
          }),
        incidentDetails: incidentDetails.trim(),
      }),
    [matchRef, phase, previewMinute, people, incidentDetails],
  );

  function focusFirst(field: CreateField): void {
    const target: HTMLElement | null =
      field === "people"
        ? peopleRef.current
        : field === "charge"
          ? (chargeRef.current?.querySelector("select, input") as HTMLElement | null)
          : field === "details"
            ? detailsRef.current
            : field === "incidentAt"
              ? incidentRef.current
              : field === "reportReceivedAt"
                ? reportRef.current
                : hearingRef.current;
    target?.focus();
  }

  function refuse(errors: Partial<Record<CreateField, string>>, summary: string): void {
    setFieldErrors(errors);
    setInvalid(summary);
    const first = (
      ["people", "charge", "details", "incidentAt", "reportReceivedAt", "hearingAt"] as const
    ).find((field) => errors[field] !== undefined);
    if (first !== undefined) focusFirst(first);
  }

  function submit(): void {
    const errors: Partial<Record<CreateField, string>> = {};

    const namedPeople: HearingPersonView[] = people
      .filter((person) => person.name.trim() !== "")
      .map((person) => ({
        name: person.name.trim(),
        role: person.role,
        shirtNumber:
          person.shirtNumber.trim() === "" ? null : Number.parseInt(person.shirtNumber, 10),
        gender: person.gender,
        teamMemberId: person.teamMemberId,
        enteredByHand: person.enteredByHand,
      }));
    if (namedPeople.length === 0) errors.people = CREATE_ERRORS.noPerson;

    const first = charges[0];
    if (first === undefined || first.article.trim() === "") {
      errors.charge = CREATE_ERRORS.noCharge;
    } else if (first.articleText.trim() === "") {
      errors.charge = CHARGE_WORDING_ERROR;
    }

    if (incidentDetails.trim() === "") errors.details = CREATE_ERRORS.noDetails;

    const incident = combineDateAndTime(incidentDate, incidentTime);
    const received = combineDateAndTime(reportDate, reportTime);
    const hearing = combineDateAndTime(hearingDate, hearingTime);

    if (incident.kind === "partial") errors.incidentAt = HALF_INSTANT_ERROR[incident.missing];
    else if (incident.kind !== "instant") errors.incidentAt = INSTANT_ERROR;
    if (received.kind === "partial") errors.reportReceivedAt = HALF_INSTANT_ERROR[received.missing];
    else if (received.kind !== "instant") errors.reportReceivedAt = INSTANT_ERROR;
    // The hearing may genuinely not be scheduled yet, so empty is an answer — half is not.
    if (hearing.kind === "partial") errors.hearingAt = HALF_INSTANT_ERROR[hearing.missing];
    else if (hearing.kind === "invalid") errors.hearingAt = HALF_INSTANT_ERROR.date;

    if (Object.keys(errors).length > 0) {
      // The one message that is fully self-explanatory on its own stays the summary, so a
      // reader who knows the form is not sent hunting for a red border he can already name.
      const only = Object.values(errors);
      refuse(errors, only.length === 1 ? (only[0] ?? SUMMARY_ERROR) : SUMMARY_ERROR);
      return;
    }

    const kept: HearingChargeView[] = charges
      .filter((charge) => charge.article.trim() !== "" && charge.articleText.trim() !== "")
      .map((charge) => ({
        article: charge.article.trim(),
        level: charge.level,
        articleText: charge.articleText.trim(),
        seriousness: charge.seriousness,
      }));

    setInvalid(null);
    setFieldErrors({});
    onCreate({
      competitionId,
      teamCode: teamCode === "" ? null : teamCode,
      personsInvolved: namedPeople,
      matchRef,
      matchDate: matchDate === "" ? null : matchDate,
      matchPhase: phase,
      // Never sent with the off-pitch phase, whatever was typed before the phase changed.
      matchMinute:
        offPitch || matchMinute.trim() === "" ? null : Number.parseInt(matchMinute, 10),
      charges: kept,
      incidentDetails: incidentDetails.trim(),
      hearingAt: hearing.kind === "instant" ? hearing.iso : null,
      hearingLocation: hearingLocation.trim() === "" ? null : hearingLocation.trim(),
      reportReceivedAt: received.kind === "instant" ? received.iso : "",
      incidentAt: incident.kind === "instant" ? incident.iso : "",
      teamManagerName: teamManagerName === "" ? null : teamManagerName,
    });
  }

  return (
    <div
      className="hmodal"
      data-testid="createBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        className="hmodal__box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="createTitle"
        data-testid="createModal"
      >
        <div className="panel__head">
          <h3 className="panel__title" id="createTitle">
            Create New Hearing
          </h3>
          <span className="panel__note">
            The TD Has Decided This Incident Is Worth A Code Of Conduct Hearing
          </span>
          <span className="spacer" />
          <button type="button" className="gbtn gbtn--sm" onClick={onCancel}>
            Close
          </button>
        </div>

        <div className="hmodal__body">
          <div className="formgrid">
            <label className="field">
              <span className="field__label">Competition</span>
              <select
                value={competitionId}
                onChange={(event) => {
                  changeCompetition(event.target.value as TmsCompetitionId);
                }}
              >
                <option value="1916">Men&rsquo;s · Competition #1916</option>
                <option value="1917">Women&rsquo;s · Competition #1917</option>
              </select>
            </label>

            <div className="field">
              <label className="field__label" htmlFor="createTeam">
                Team{" "}
                {/* A dropdown cannot carry a flag inside its options, so the chosen team's
                    flag is drawn beside the label — which is where the eye goes to check it. */}
                {teamCode === "" ? null : <CountryFlag code={teamCode} className="tmflag" />}
              </label>
              <select
                id="createTeam"
                value={teamChoice}
                onChange={(event) => {
                  changeTeam(event.target.value);
                }}
              >
                <option value="">No team named</option>
                {competitionTeams.map((team) => (
                  <option key={team.code} value={team.code}>
                    {team.code} · {team.name}
                  </option>
                ))}
                <option value={TYPED_TEAM}>Team not listed — type the code</option>
              </select>
              {teamChoice === TYPED_TEAM ? (
                <input
                  aria-label="Team code"
                  autoComplete="off"
                  placeholder="IOC code, for example KOR"
                  value={typedTeamCode}
                  onChange={(event) => {
                    setTypedTeamCode(event.target.value);
                  }}
                />
              ) : null}
              {competitionTeams.length === 0 ? (
                <span className="field__sub">
                  The team list could not be read, so type the code in instead.
                </span>
              ) : null}
            </div>

            <div className="field">
              <label className="field__label" htmlFor="createManager">
                Team Manager
              </label>
              <span className="field__sub">
                The Hearing Letter is addressed to the Team Manager by name. Choosing a team
                fills this in from that team&rsquo;s officials; change it if somebody else is
                acting. Leave it empty if you do not know yet — the case can still be opened,
                and the letter cannot be drafted until it is named.
              </span>
              <select
                id="createManager"
                value={managerChoice}
                onChange={(event) => {
                  setManagerChoice(event.target.value);
                }}
              >
                <option value="">
                  {teamCode === ""
                    ? "Choose a team first"
                    : membersState === "loading"
                      ? "Reading the team list…"
                      : "Not named yet"}
                </option>
                {teamOfficials.map((member) => (
                  <option key={member.id} value={member.id}>
                    {memberOptionLabel(member)}
                  </option>
                ))}
                <option value={TYPED_MANAGER}>Not on the list — type the name</option>
              </select>
              {managerChoice === TYPED_MANAGER ? (
                <input
                  aria-label="Team Manager name"
                  autoComplete="off"
                  placeholder="Team Manager name"
                  value={typedManager}
                  onChange={(event) => {
                    setTypedManager(event.target.value);
                  }}
                />
              ) : null}
              {membersState === "failed" ? (
                <span className="field__sub">
                  The team list could not be read. Type the name in by hand.
                </span>
              ) : membersState === "ready" && findTeamManager(members) === null ? (
                <span className="field__sub" data-testid="noManagerOnList">
                  Nobody on this team&rsquo;s list is recorded as the Team Manager, so nothing
                  has been filled in. Pick an official or type the name.
                </span>
              ) : null}
            </div>
          </div>

          <fieldset
            className={fieldErrors.people === undefined ? "hmodal__group" : "hmodal__group hmodal__group--bad"}
          >
            <legend className="field__label">Team Members Involved</legend>
            <p className="field__sub">
              Choose the role first, then the person. Several people can be involved in one
              incident — add a row for each. Whoever is named here is named in the letter and
              in the minutes, so their details are copied onto the case now and stay as they
              are, whatever the team list says later.
            </p>
            {people.map((person, index) => (
              <div className="formgrid" key={index}>
                <label className="field">
                  <span className="field__sub">Role</span>
                  <select
                    aria-label={`Person ${index + 1} role`}
                    value={person.role}
                    onChange={(event) => {
                      changeRole(index, event.target.value as HearingPersonRole);
                    }}
                  >
                    {FORM_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="field">
                  <span className="field__sub">Name</span>
                  {person.enteredByHand ? (
                    <input
                      aria-label={`Person ${index + 1} name`}
                      value={person.name}
                      autoComplete="off"
                      onChange={(event) => {
                        updatePerson(index, { name: event.target.value });
                      }}
                    />
                  ) : (
                    <select
                      aria-label={`Person ${index + 1} name`}
                      ref={index === 0 ? peopleRef : null}
                      value={person.teamMemberId ?? ""}
                      onChange={(event) => {
                        pickPerson(index, event.target.value);
                      }}
                    >
                      <option value="">
                        {teamCode === ""
                          ? "Choose a team first"
                          : membersState === "loading"
                            ? "Reading the team list…"
                            : "Choose a name"}
                      </option>
                      {members
                        .filter((member) => member.role === memberRoleOf(person.role))
                        .map((member) => (
                          <option key={member.id} value={member.id}>
                            {memberOptionLabel(member)}
                          </option>
                        ))}
                      <option value={TYPED_PERSON}>Not on the list — type by hand</option>
                    </select>
                  )}
                  {person.enteredByHand ? (
                    <span className="field__sub" data-testid={`byhand-${String(index)}`}>
                      Typed in by hand — the case record will show that.{" "}
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => {
                          updatePerson(index, {
                            ...emptyPerson(),
                            role: person.role,
                          });
                        }}
                      >
                        Choose from the team list instead
                      </button>
                    </span>
                  ) : null}
                  {membersState === "failed" ? (
                    <span className="field__sub">
                      The team list could not be read. Type the person in by hand.
                    </span>
                  ) : null}
                </div>

                <label className="field">
                  <span className="field__sub">Shirt Number</span>
                  <input
                    aria-label={`Person ${index + 1} shirt number`}
                    inputMode="numeric"
                    value={person.shirtNumber}
                    disabled={person.role === "Team Official"}
                    autoComplete="off"
                    onChange={(event) => {
                      updatePerson(index, { shirtNumber: event.target.value });
                    }}
                  />
                </label>

                <label className="field">
                  <span className="field__sub">Gender</span>
                  <select
                    aria-label={`Person ${index + 1} gender`}
                    value={person.gender}
                    onChange={(event) => {
                      updatePerson(index, { gender: event.target.value as "m" | "f" });
                    }}
                  >
                    <option value="m">Male</option>
                    <option value="f">Female</option>
                  </select>
                </label>
              </div>
            ))}
            <p className="field__sub">
              The gender is asked for because the minutes and the decision letter say{" "}
              <i>him</i> or <i>her</i>, and getting that wrong in a disciplinary letter is the
              kind of mistake the archived cases are full of.
            </p>
            {fieldErrors.people === undefined ? null : (
              <span className="field__bad" role="alert" data-testid="err-people">
                {fieldErrors.people}
              </span>
            )}
            <button
              type="button"
              className="gbtn gbtn--sm"
              onClick={() => {
                setPeople((rows) => [...rows, emptyPerson()]);
              }}
            >
              Add Another Person
            </button>
          </fieldset>

          <div className="formgrid">
            <div className="field">
              <label className="field__label" htmlFor="createMatch">
                Match Number · Optional
              </label>
              <span className="field__sub">
                Leave it as no match where the incident happened away from a game. Name a match
                where the game is the context even though the incident happened off the pitch —
                comments about the officials after a game belong to that game.
              </span>
              <select
                id="createMatch"
                value={matchChoice}
                onChange={(event) => {
                  chooseMatch(event.target.value);
                }}
              >
                <option value={NO_MATCH}>No match — the incident was off the pitch</option>
                {competitionFixtures.map((fixture) => (
                  <option key={fixture.tmsMatchDbId} value={String(fixture.tmsMatchDbId)}>
                    {fixtureOptionLabel(fixture)}
                  </option>
                ))}
                <option value={TYPED_MATCH}>Match not listed — type the number</option>
              </select>
              {matchChoice === TYPED_MATCH ? (
                <input
                  aria-label="Match number"
                  autoComplete="off"
                  placeholder="For example M09 or W24"
                  value={typedMatchRef}
                  onChange={(event) => {
                    setTypedMatchRef(event.target.value);
                  }}
                />
              ) : null}
            </div>
            <label className="field">
              <span className="field__label">Match Date · Optional</span>
              <span className="field__sub">
                {chosenFixture === null
                  ? "Give the date where you have typed a match number in yourself."
                  : "Filled in from the match you chose."}
              </span>
              <input
                type="date"
                aria-label="Match date"
                value={matchDate}
                disabled={chosenFixture !== null}
                onChange={(event) => {
                  setMatchDate(event.target.value);
                }}
              />
            </label>
          </div>

          <div className="formgrid">
            <label className="field">
              <span className="field__label">When It Happened</span>
              <span className="field__sub">
                Every moment of a match, and off the pitch outside match time — an incident
                after the final whistle, in the team hotel or on social media is recorded as
                such, not left blank.
              </span>
              <select
                value={phase}
                onChange={(event) => {
                  setPhase(event.target.value as HearingMatchPhase);
                }}
              >
                {PHASES.map((one) => (
                  <option key={one} value={one}>
                    {one}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Minute</span>
              <span className="field__sub">
                {offPitch
                  ? "Not applicable — this says the incident did not happen during play, so there is no minute of play to record."
                  : "A whole minute — the record stores a minute, not a clock time."}
              </span>
              {/*
               * DISABLED, AND CLEARED, WHEN THE PHASE IS OFF-PITCH (ruling §J).
               *
               * The route refuses a minute alongside that phase — a minute is a match-time
               * claim and the phase denies one — so leaving the field live would let the form
               * assemble a body it knows will be rejected. It is cleared as well as disabled,
               * because a minute typed and then hidden is a minute the sender still believes
               * was sent.
               */}
              <input
                inputMode="numeric"
                aria-label="Match minute"
                value={offPitch ? "" : matchMinute}
                disabled={offPitch}
                autoComplete="off"
                onChange={(event) => {
                  setMatchMinute(event.target.value);
                }}
              />
            </label>
          </div>

          <div
            ref={chargeRef}
            className={fieldErrors.charge === undefined ? undefined : "chargeblock--bad"}
          >
            <ChargePicker charges={charges} onChange={updateCharge} />
            {fieldErrors.charge === undefined ? null : (
              <span className="field__bad" role="alert" data-testid="err-charge">
                {fieldErrors.charge}
              </span>
            )}
          </div>

          <div className={fieldErrors.details === undefined ? "field" : "field field--bad"}>
            <label className="field__label" htmlFor="createDetails">
              Incident Details
            </label>
            <textarea
              id="createDetails"
              ref={detailsRef}
              value={incidentDetails}
              aria-invalid={fieldErrors.details === undefined ? undefined : true}
              placeholder="What happened, in plain factual terms. This text is carried into the Hearing Letter and the minutes, so write it as the record."
              onChange={(event) => {
                setIncidentDetails(event.target.value);
                clearField("details");
              }}
            />
            {fieldErrors.details === undefined ? null : (
              <span className="field__bad" role="alert" data-testid="err-details">
                {fieldErrors.details}
              </span>
            )}
            <span className="field__sub">{DETAILS_HINT}</span>
          </div>

          {/*
           * THE SENTENCE THE TD IS ABOUT TO CREATE (§14, ruled 2026-09-10).
           *
           * The house preview idiom — a `.composerlabel` kicker over a `.previewbox`, as the
           * report composers use — because it is the same promise: this is the assembled text,
           * it is not editable here, and it is what will go out.
           */}
          <div className="composerprev incidentprev" data-testid="incident-preview-block">
            <span className="composerlabel">{PREVIEW_LABEL}</span>
            <pre className="previewbox" data-testid="incident-preview">
              {previewSentence === "" ? PREVIEW_EMPTY : previewSentence}
            </pre>
          </div>

          <div className="formgrid">
            <DateTimeField
              label="Time Of The Incident"
              hint="When the incident actually happened. The 24-hour rule is counted from it."
              date={incidentDate}
              time={incidentTime}
              dateRef={incidentRef}
              invalid={fieldErrors.incidentAt !== undefined}
              message={fieldErrors.incidentAt}
              onChangeDate={(value) => {
                setIncidentDate(value);
                clearField("incidentAt");
              }}
              onChangeTime={(value) => {
                setIncidentTime(value);
                clearField("incidentAt");
              }}
            />
            <DateTimeField
              label="Report Received"
              hint="When the report actually reached you. It is never assumed to be now."
              date={reportDate}
              time={reportTime}
              dateRef={reportRef}
              invalid={fieldErrors.reportReceivedAt !== undefined}
              message={fieldErrors.reportReceivedAt}
              onChangeDate={(value) => {
                setReportDate(value);
                clearField("reportReceivedAt");
              }}
              onChangeTime={(value) => {
                setReportTime(value);
                clearField("reportReceivedAt");
              }}
            />
          </div>

          <div className="formgrid">
            <DateTimeField
              label="Time Of The Hearing"
              hint="Leave both boxes empty if the hearing is not scheduled yet."
              date={hearingDate}
              time={hearingTime}
              dateRef={hearingRef}
              invalid={fieldErrors.hearingAt !== undefined}
              message={fieldErrors.hearingAt}
              onChangeDate={(value) => {
                setHearingDate(value);
                clearField("hearingAt");
              }}
              onChangeTime={(value) => {
                setHearingTime(value);
                clearField("hearingAt");
              }}
            />
            <label className="field">
              <span className="field__label">Location Of The Hearing</span>
              <span className="field__sub">
                {defaultLocation === null
                  ? "No hearing venue is set in Tournament Configuration, so type one in."
                  : "Filled in from the hearing venue in Tournament Configuration. Change it for this case if you are meeting somewhere else."}
              </span>
              <input
                aria-label="Location Of The Hearing"
                value={hearingLocation}
                autoComplete="off"
                onChange={(event) => {
                  setHearingLocation(event.target.value);
                }}
              />
            </label>
          </div>
        </div>

        <div className="panel__foot hmodal__acts">
          {invalid === null ? null : (
            <p className="rowerror" role="alert" data-testid="createError">
              {invalid}
            </p>
          )}
          {error === null ? null : (
            <p className="rowerror" role="alert" data-testid="createApiError">
              {error.message}
            </p>
          )}
          <span className="spacer" />
          <button type="button" className="gbtn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="gbtn gbtn--solid" disabled={busy} onClick={submit}>
            {busy ? "Creating…" : "Create Case"}
          </button>
        </div>
      </div>
    </div>
  );
}
